import React from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button,
} from '@mui/material';
import { RocketLaunch, Login, PersonAdd } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

/**
 * LoginRequiredModal
 * Shown when a guest user tries a restricted feature via requireAuth().
 * Offers two CTAs: Sign In and Create Account — both open the AuthModal.
 */
export default function LoginRequiredModal() {
  const { authModal, closeAuthModal, openAuthModal } = useAuth();

  // This modal fires only when guardMessage is set
  const isOpen = authModal.open && Boolean(authModal.guardMessage);

  const handleLogin = () => {
    closeAuthModal();
    // Small delay so the close animation finishes before the new modal opens
    setTimeout(() => openAuthModal(0), 150);
  };

  const handleRegister = () => {
    closeAuthModal();
    setTimeout(() => openAuthModal(1), 150);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={closeAuthModal}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(145deg, rgba(5,13,26,0.97) 0%, rgba(10,22,40,0.97) 100%)',
          backdropFilter: 'blur(30px)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Top accent glow */}
      <Box sx={{
        height: 3,
        background: 'linear-gradient(90deg, #673ab7, #4fc3f7, #0288d1)',
      }} />

      <DialogContent sx={{ p: 4, textAlign: 'center' }}>

        {/* Icon */}
        <Box sx={{
          display: 'inline-flex', p: 2, borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.2)',
          mb: 2,
        }}>
          <RocketLaunch sx={{ fontSize: 40, color: 'primary.main' }} />
        </Box>

        {/* Title */}
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.light', mb: 1 }}>
          Login Required
        </Typography>

        {/* Message from the calling context */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
          {authModal.guardMessage || 'Login required to use this feature 🚀'}
        </Typography>

        {/* Stars decoration */}
        <Box sx={{
          mb: 3, fontSize: 18, letterSpacing: 6,
          color: 'rgba(255,255,255,0.4)',
        }}>
          ✦ ✦ ✦
        </Box>

        {/* CTAs */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Login />}
            onClick={handleLogin}
            sx={{ px: 3, py: 1, fontWeight: 700, borderRadius: 2, minWidth: 120 }}
          >
            Sign In
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonAdd />}
            onClick={handleRegister}
            sx={{
              px: 3, py: 1, fontWeight: 700, borderRadius: 2, minWidth: 140,
              borderColor: 'rgba(255,255,255,0.4)',
              color: 'primary.light',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            Create Account
          </Button>
        </Box>

        {/* Dismiss */}
        <Typography
          variant="caption"
          onClick={closeAuthModal}
          sx={{
            display: 'block', mt: 2.5, color: 'text.secondary',
            cursor: 'pointer', '&:hover': { color: 'text.primary' },
          }}
        >
          Continue browsing as guest
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
